import { api } from '../lib/api'
import { resolveMediaUrl } from '../lib/mediaUrl'

export type PeopleReader = {
  hero: {
    displayName: string
    handle: string
    avatar: string
    coverImage?: string
    motto?: string
    verified?: boolean
    reputation_tier?: string
    reputation_score?: number
  }
  about?: {
    shortBio?: string
    long?: { html?: string; plain?: string }
  }
  story?: {
    title?: string
    subtitle?: string
    cover?: { portrait?: string; landscape?: string }
    body?: { html?: string; plain?: string; json?: any }
  }
  personal?: {
    livesIn?: string
    from?: string
    languages?: string[]
    occupation?: string
  }
  links?: { label: string; url: string }[]
  stats?: { followers?: number; following?: number; reactions?: number; gifts?: number }
  total_gifts_received_pixe?: number
  total_gifts_received_pixe_atomic?: string
  reputation_tier?: string
  reputation_score?: number
  appreciation_score?: number
  followers_count?: number
  following_count?: number
  is_following?: boolean
  counts?: { likes?: number; saves?: number; shares?: number }
  // Optional rich sections mirrored from mobile People Reader
  works?: { featured?: any[]; recent?: any[] }
  photos_enabled?: boolean
  photo_albums?: Array<{
    id: string
    title: string
    sort_order: number
    visibility: 'public' | 'subscribers' | 'ppv' | string
    is_locked: boolean
    is_unlocked?: boolean
    unlock_price_pixe?: number | null
    photo_count: number
    preview_photos: Array<{
      id: string
      thumb_url_512: string
      caption?: string
      is_featured?: boolean
    }>
  }>
  photos?: Array<{
    id: string
    album_id?: string | null
    image_url: string
    thumb_url_512?: string
    caption?: string
    sort_order: number
    is_featured?: boolean
    width?: number | null
    height?: number | null
  }>
  viewer?: { is_subscriber?: boolean }
  reviews?: any[]
  profileComments?: any[]
  comments?: { written?: any[] }
  actions?: { canFollow?: boolean; canMessage?: boolean; isFollowing?: boolean; isSelf?: boolean }
  education?: any[]
  employment?: any[]
  skills?: { name: string }[]
  pixe_pros?: {
    is_enabled: boolean
    pro_mode: 'REMOTE' | 'LOCAL' | 'BOTH'
    title?: string
    bio?: string
    service_description?: string
    hourly_rate_cents?: number | null
    base_rate_cents?: number | null
    currency?: string
    service_radius_miles?: number
    available_now?: boolean
    average_rating?: number
    rating_count?: number
    completed_jobs_count?: number
    total_earned_pixe?: string
    skills?: string[]
    company_role_title?: string
    company?: {
      id: number
      slug: string
      title: string
      avatar_url?: string
      cover_url?: string
    } | null
  } | null
  _meta: { public_id: string; handle?: string }
}

function normalizePeopleReader(raw: PeopleReader): PeopleReader {
  const hero = raw?.hero || ({} as PeopleReader['hero'])
  const story = raw?.story
  return {
    ...raw,
    hero: {
      ...hero,
      avatar: resolveMediaUrl(hero.avatar) || hero.avatar || '',
      coverImage: resolveMediaUrl(hero.coverImage) || hero.coverImage || undefined,
    },
    story: story
      ? {
          ...story,
          cover: story.cover
            ? {
                ...story.cover,
                portrait: resolveMediaUrl(story.cover.portrait) || story.cover.portrait,
                landscape: resolveMediaUrl(story.cover.landscape) || story.cover.landscape,
              }
            : story.cover,
        }
      : story,
  }
}

export async function fetchPeopleReader(publicId: string): Promise<PeopleReader> {
  const { data } = await api.get<PeopleReader>(`/api/people/i/${publicId}/`)
  return normalizePeopleReader(data)
}

export async function fetchPeopleReaderByUserId(userId: number): Promise<PeopleReader> {
  const { data } = await api.get<PeopleReader>(`/api/people/by-id/${userId}/`)
  return normalizePeopleReader(data)
}
